import React, { useEffect } from 'react';
import {
  Button,
  Card,
  Col,
  Collapse,
  Form,
  Input,
  Row,
  Select,
  Space,
  Switch,
  message,
} from 'antd';
import {
  EditOutlined,
  EnvironmentOutlined,
  IdcardOutlined,
  SaveOutlined,
  CarOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import Api from '../../../Network';

interface CustomerEditFormProps {
  customer: any;
  onUpdated?: () => void;
}

const CustomerEditForm: React.FC<CustomerEditFormProps> = ({ customer, onUpdated }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  useEffect(() => {
    const billing = customer?.billing_address || {};
    const shipping = customer?.shipping_address || {};
    form.setFieldsValue({
      name: customer?.name || '',
      organization: customer?.organization || customer?.trade_name || '',
      manager_name: customer?.manager_name || '',
      commercial_registration_number: customer?.commercial_registration_number || '',
      email: customer?.email || '',
      phone_number: customer?.phone_number || '',
      tax_number: customer?.tax_number || '',
      is_vat_registered: !!customer?.is_vat_registered,
      status: customer?.status || 'Pending',
      signer_name: customer?.signer_name || '',
      signer_title: customer?.signer_title || '',
      billing_building_number: billing.building_number || '',
      billing_address: billing.billing_address || '',
      billing_district: billing.billing_district || billing.billing_state || '',
      billing_city: billing.billing_city || '',
      billing_zip: billing.billing_zip || '',
      billing_country: billing.billing_country || 'Saudi Arabia',
      billing_additional_number: billing.additional_number || '',
      shipping_building_number: shipping.shipping_building_number || '',
      shipping_address: shipping.shipping_address || '',
      shipping_state: shipping.shipping_state || '',
      shipping_city: shipping.shipping_city || '',
      shipping_zip: shipping.shipping_zip || '',
      shipping_country: shipping.shipping_country || 'Saudi Arabia',
    });
  }, [customer, form]);

  const mutation = useMutation({
    mutationFn: (payload: any) => Api.put(`/api/admin/customers/${customer.id}`, payload),
    onSuccess: (res: any) => {
      const warning = res?.data?.qoyod_warning || res?.qoyod_warning;
      if (warning) {
        message.warning(
          t('customers.edit.savedLocalQoyodFailed', 'Saved locally, Qoyod update failed: {{msg}}', {
            msg: warning,
          })
        );
      } else {
        message.success(t('customers.edit.success', 'Customer data updated'));
      }
      onUpdated?.();
    },
    onError: (error: any) => {
      const msg =
        error?.response?.data?.msg ||
        error?.response?.data?.message ||
        t('customers.edit.error', 'Failed to update customer');
      message.error(Array.isArray(msg) ? msg.join(', ') : msg);
    },
  });

  const submit = async () => {
    const values = await form.validateFields();
    mutation.mutate({
      name: values.name,
      organization: values.organization,
      trade_name: values.organization,
      manager_name: values.manager_name,
      commercial_registration_number: values.commercial_registration_number,
      email: values.email || null,
      phone_number: values.phone_number,
      tax_number: values.tax_number || null,
      is_vat_registered: !!values.is_vat_registered,
      status: values.status,
      signer_name: values.signer_name,
      signer_title: values.signer_title,
      billing_address: {
        building_number: values.billing_building_number,
        billing_address: values.billing_address,
        billing_district: values.billing_district,
        billing_state: values.billing_district,
        billing_city: values.billing_city,
        billing_zip: values.billing_zip,
        billing_country: values.billing_country,
        additional_number: values.billing_additional_number,
      },
      shipping_address: {
        shipping_building_number: values.shipping_building_number,
        building_number: values.shipping_building_number,
        shipping_address: values.shipping_address,
        shipping_state: values.shipping_state,
        shipping_city: values.shipping_city,
        shipping_zip: values.shipping_zip,
        shipping_country: values.shipping_country,
      },
    });
  };

  const basicFields = (
    <Row gutter={[16, 0]}>
      <Col xs={24} md={12}>
        <Form.Item
          name="name"
          label={t('customers.details.name', 'Company name')}
          rules={[{ required: true }]}
        >
          <Input />
        </Form.Item>
      </Col>
      <Col xs={24} md={12}>
        <Form.Item name="organization" label={t('customers.details.tradingName', 'Trading name')}>
          <Input />
        </Form.Item>
      </Col>
      <Col xs={24} md={12}>
        <Form.Item name="manager_name" label={t('customers.details.manager', 'Manager')}>
          <Input />
        </Form.Item>
      </Col>
      <Col xs={24} md={12}>
        <Form.Item
          name="commercial_registration_number"
          label={t('customers.details.crNumber', 'CR number')}
        >
          <Input />
        </Form.Item>
      </Col>
      <Col xs={24} md={12}>
        <Form.Item
          name="phone_number"
          label={t('customers.details.phone', 'Phone')}
          rules={[{ required: true }]}
        >
          <Input />
        </Form.Item>
      </Col>
      <Col xs={24} md={12}>
        <Form.Item name="email" label={t('customers.details.email', 'Email')}>
          <Input />
        </Form.Item>
      </Col>
      <Col xs={24} md={12}>
        <Form.Item name="tax_number" label={t('customers.details.taxNumber', 'Tax number')}>
          <Input />
        </Form.Item>
      </Col>
      <Col xs={24} md={12}>
        <Form.Item
          name="is_vat_registered"
          label={t('customers.details.vatRegistered', 'VAT registered')}
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
      </Col>
      <Col xs={24} md={12}>
        <Form.Item name="status" label={t('customers.columns.status', 'Status')}>
          <Select
            options={[
              { label: t('customers.status.pending', 'Pending'), value: 'Pending' },
              { label: t('customers.status.active', 'Active'), value: 'Active' },
              { label: t('customers.status.inactive', 'Inactive'), value: 'Inactive' },
            ]}
          />
        </Form.Item>
      </Col>
      <Col xs={24} md={12}>
        <Form.Item name="signer_name" label={t('customers.details.signer', 'Signer')}>
          <Input />
        </Form.Item>
      </Col>
      <Col xs={24} md={12}>
        <Form.Item name="signer_title" label={t('customers.details.signerTitle', 'Signer title')}>
          <Input />
        </Form.Item>
      </Col>
    </Row>
  );

  const billingFields = (
    <Row gutter={[16, 0]}>
      <Col xs={24} md={8}>
        <Form.Item
          name="billing_building_number"
          label={t('customers.edit.buildingNumber', 'Building number')}
        >
          <Input />
        </Form.Item>
      </Col>
      <Col xs={24} md={16}>
        <Form.Item name="billing_address" label={t('customers.edit.street', 'Street')}>
          <Input />
        </Form.Item>
      </Col>
      <Col xs={24} md={8}>
        <Form.Item name="billing_district" label={t('customers.edit.district', 'District')}>
          <Input />
        </Form.Item>
      </Col>
      <Col xs={24} md={8}>
        <Form.Item name="billing_city" label={t('customers.city', 'City')}>
          <Input />
        </Form.Item>
      </Col>
      <Col xs={24} md={8}>
        <Form.Item name="billing_zip" label={t('customers.edit.postalCode', 'Postal code')}>
          <Input />
        </Form.Item>
      </Col>
      <Col xs={24} md={8}>
        <Form.Item
          name="billing_additional_number"
          label={t('customers.edit.additionalNumber', 'Additional number')}
        >
          <Input />
        </Form.Item>
      </Col>
      <Col xs={24} md={8}>
        <Form.Item name="billing_country" label={t('customers.country', 'Country')}>
          <Input />
        </Form.Item>
      </Col>
    </Row>
  );

  const shippingFields = (
    <Row gutter={[16, 0]}>
      <Col xs={24} md={8}>
        <Form.Item
          name="shipping_building_number"
          label={t('customers.edit.buildingNumber', 'Building number')}
        >
          <Input />
        </Form.Item>
      </Col>
      <Col xs={24} md={16}>
        <Form.Item name="shipping_address" label={t('customers.edit.street', 'Street')}>
          <Input />
        </Form.Item>
      </Col>
      <Col xs={24} md={8}>
        <Form.Item name="shipping_state" label={t('customers.edit.district', 'District')}>
          <Input />
        </Form.Item>
      </Col>
      <Col xs={24} md={8}>
        <Form.Item name="shipping_city" label={t('customers.city', 'City')}>
          <Input />
        </Form.Item>
      </Col>
      <Col xs={24} md={8}>
        <Form.Item name="shipping_zip" label={t('customers.edit.postalCode', 'Postal code')}>
          <Input />
        </Form.Item>
      </Col>
      <Col xs={24} md={8}>
        <Form.Item name="shipping_country" label={t('customers.country', 'Country')}>
          <Input />
        </Form.Item>
      </Col>
    </Row>
  );

  return (
    <Card
      className="info-card customer-edit-card"
      style={{ marginBottom: 24 }}
      title={
        <Space>
          <EditOutlined />
          {t('customers.edit.title', 'Edit customer data')}
        </Space>
      }
      extra={
        <Button
          type="primary"
          icon={<SaveOutlined />}
          loading={(mutation as any).isPending || (mutation as any).isLoading}
          onClick={submit}
        >
          {t('customers.edit.save', 'Save changes')}
        </Button>
      }
    >
      <Form form={form} layout="vertical">
        <Collapse
          accordion
          bordered={false}
          className="customer-edit-accordion"
          defaultActiveKey={[]}
          items={[
            {
              key: 'basic',
              label: (
                <Space>
                  <IdcardOutlined />
                  {t('customers.basicInformation', 'Basic Information')}
                </Space>
              ),
              children: basicFields,
            },
            {
              key: 'billing',
              label: (
                <Space>
                  <EnvironmentOutlined />
                  {t('customers.billingAddress', 'Billing address')}
                </Space>
              ),
              children: billingFields,
            },
            {
              key: 'shipping',
              label: (
                <Space>
                  <CarOutlined />
                  {t('customers.shippingAddress', 'Shipping address')}
                </Space>
              ),
              children: shippingFields,
            },
          ]}
        />
      </Form>
    </Card>
  );
};

export default CustomerEditForm;
